/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import JSEncrypt from 'jsencrypt';

import i18next from "i18next";
import loginConfig from '@/constants/loginConfig'

import { 
  judgeNewOrOldUserApi,
  getSiteTicket,
  getCountryListApi
} from "@/apis/login/login.api";

import { isBeta, isProduction } from "@/utils/multiSite/index";
import cookieUtil from "@/utils/cookieUtils";

import { EmailPropsType } from "@/app/login/type/index"

// 删除文本前后空格
export const  trimFirstAndLastSpace = (str: any) => {
  return str.replace(/^ | $/g, "");
};
/**
 * @desc 加密
 */
export const getEncryptDefaultString = (str: string) => {
  if (typeof str === "number") {
    str = `${str}`;
  }
  return encrypt(str);
  // return typeof str === "string" && str.indexOf("*") === -1 ? encrypt(str) : undefined;
};
export function encrypt(args: string) {
  const jsEncrypt = new JSEncrypt();
  jsEncrypt.setPublicKey(loginConfig.PUB_KEY);
  const str = typeof args == "string" ? args : JSON.stringify(args);
  return encodeURIComponent(jsEncrypt.encrypt(str));
}

/**
 * @desc 获取当前端的语言Cookie(Get the language cookie of the current end)
 * [注] Cookie由configon配置，如果获取及解析configon报错，则通过此函数动态计算cookie的key
 * [Note] Cookie is configured by configon. If an error is reported when obtaining and parsing configon, the cookie key is dynamically calculated through this function.
 */
export function getClient() {
  let result = "";
  let pathname = location.pathname;
  if (pathname.indexOf(`/seller/`) > -1) {
    result = 'seller'
  }
  // C(user)端
  else if (pathname.indexOf(`/user/`) > -1) {
    result = 'user'
  }
  // E(oper)端
  else if (pathname.indexOf(`/oper/`) > -1) {
    result = 'oper'
  }
  // EJ(jnos)端
  else if (pathname.indexOf(`/jnos/`) > -1) {
    result = 'jnos'
  }
  // BO(open)端
  else if (pathname.indexOf(`/open/`) > -1) {
    result = 'open'
  }
  return result;
}


export const isValidatedEmail = (str: string) => {
  const emailRegex = /^[A-Za-z0-9._+-]+@[A-Za-z0-9._+-]+\.[A-Za-z._+-]{2,}$/; // 邮箱正则表达式
  return emailRegex.test(str);
};

// 获取缓存的用户信息
export const getAccountInfo = () => {
  const account = cookieUtil.getCookie(loginConfig.loginLocalInfo.USER_SUCCESS_ACCOUNT);
  let accountInfo = {};
  if (account) {
    if (isValidatedEmail(account)) {
      accountInfo = {
        userType: "old",
        phoneOrEmail: loginConfig.LOGIN.EMAIL_LOGIN,
        identifier: account,
      };
    } else {
      const [countryCode, localNumber] = account.split("_");
      accountInfo = {
        userType: "old",
        phoneOrEmail: loginConfig.LOGIN.PHONE_LOGIN,
        idPrefix: countryCode,
        identifier: localNumber,
      };
    }
  }
  return accountInfo; 
};

// 获取当前账号新用户还是老用户
export const judgeNewOrOldUserFn = async (params: {
  type: string;
  identifier: string | number;
  idPrefix?: string;
}) => {
  const res: any = await judgeNewOrOldUserApi({
    // 2 代表手机号, 3 代表邮箱
    otpType: params.type === "phone" ? 2 : 3,
    identifier: params.identifier,
    idPrefix: params.type === "phone" ? params.idPrefix || "" : "",
  });
  let userType = "";
  // exist true 表示当前账号是新用户，存在表示老用户
  if (res && res?.data?.exist) {
    userType = "old";
  } else {
    userType = "new";
  }
  // wl 表示是否是白名单, 白名单: true 不进行 Google 验证; 非白名单: false 进行 Google 验证
  return { userType, riskType: res?.data?.riskType };
};

// 登录注册账号输入的时候邮箱校验；
export const validateEmail = (email: string) => {
  if (!isValidatedEmail(email)) {
    return false;
  }
  // 检查 @ 符号之前的部分长度
  const localPart = email.split("@")[0];
  if (localPart.length < 2 || localPart.length > 64) {
    return false;
  }
  // 检查整个电子邮件地址的长度
  if (email.length > 254) {
    return false;
  }
  // 如果所有检查都通过，则电子邮件地址有效
  return true;
};

// 提取国际手机号 + 后的数字；
export const extractPhoneNumber = (phone: string) => {
  const regex = /\+(\d+)/;
  const match = phone.match(regex);
  if (match && match[1]) {
    return match[1];
  }
  return null;
};

// 邮箱登录参数提取
export const extractEmailParams = (props: EmailPropsType) => {
  let data: EmailPropsType;
  if (props.userType === "new") {
    data = {
      email: props.identifier,
      password: props.password,
      appId: loginConfig.LOGIN.LOGIN_APPID,
      captchaAction: loginConfig.CaptchaActionEnum.SIGNUP,
    };
  } else {
    data = {
      identifier: props.identifier,
      password: props.password,
      loginType: loginConfig.LoginTypeEnum.EMAIL,
      otpType: loginConfig.LoginTypeEnum.EMAIL,
      captchaAction: loginConfig.CaptchaActionEnum.SIGNUP,
    };
  }
  if (props.sessionId) {
    data.sessionId = props.sessionId;
  }
  if (props.captchaCode) {
    data.captchaCode = props.captchaCode;
  }
  return data;
};

// 获取国家列表
export const getCountryList = async () => {
  const [err, res] = await getCountryListApi();
  if (res?.success) {
    const {
      internationalData: {
        defaultMobileLocations = [],
        mobileLocations = []
      } = {}
    } = res?.data;
    return {
      mobileLocations
    }
  }
  return {
    defaultMobileLocations: [],
    mobileLocations: []
  }
};

// 邮箱列表
export const emailList: Array<string> = [
  "@gmail.com", 
  "@hotmail.com", 
  "@yahoo.com", 
  "@yahoo.co.uk", 
  "@icloud.com", 
  "@msn.com", 
  "@live.com", 
  "@qq.com",
  "@btinternet.com", 
  "@virginmedia.com", 
  "@btinternet.com", 
  "@live.co.uk", 
  "@sky.com", 
  "@talktalk.co.uk", 
  "@blueyonder.co.uk", 
  "@ntlworld.com", 
  "@me.com"
]

// 获取url 中 ext_ 参数
export function splitAndProcess() {
  const url = window.location.href.split("?")[1];
  if (!url) {
    return;
  }
  const data = url.split('&');
  if(data && data.length == 0) {
    return;
  }
  const list = data.map((part: any) => {
    const [key, value] = part.split('=');
    return { key, value};
  });
  let obj: any = {};
  list && list.length > 0 && list.forEach((item: any) => {
    if (item.key.startsWith('ext_')) {
      obj[item.key.replace(/^ext_/, '')] = item.value;
    }
  });
  return obj;
}

// 邮箱输入密码校验规则
export const validateEmailPassword = (psdValue: string): {
  errors: {
    minimumMsg: string;
    atLeastOneNumberMsg: string;
    atLeastOneLetterMsg: string;
    atLeastOneSpecialCharacterMsg: string;
  };
  minimumValid: boolean;
  atLeastOneNumberValid: boolean;
  atLeastOneLetterValid: boolean;
  atLeastOneSpecialCharacterValid: boolean;
} => {
  const minimumValid = psdValue.length >= 8;
  const atLeastOneNumberValid = /[0-9]/.test(psdValue);
  const atLeastOneLetterValid = /[A-Z]/.test(psdValue);
  const atLeastOneSpecialCharacterValid = /[!@#$%^&*()_+\-=\[\]{}|\\:;"'<>,.?/~`]/.test(psdValue);

  return {
    errors: {
      minimumMsg: i18next.t("login.signin.forgotPwd.setNewPwd.rules.rule1"),
      atLeastOneNumberMsg: i18next.t("login.signin.forgotPwd.setNewPwd.rules.rule2"),
      atLeastOneLetterMsg: i18next.t("login.signin.forgotPwd.setNewPwd.rules.rule3"),
      atLeastOneSpecialCharacterMsg: i18next.t("login.signin.forgotPwd.setNewPwd.rules.rule4"),
    },
    minimumValid,
    atLeastOneNumberValid,
    atLeastOneLetterValid,
    atLeastOneSpecialCharacterValid,
  };
};

// 手机号码校验
export const phonePattern = (v: any, regStr: string) => {
  const reg = new RegExp(regStr) || /^\d{0,8}$|^\d{15,}$/;
  if (!reg.test(v)) {
    return {
      type: false,
      msgLanguageKey: "login.default.rules.validPhone"
    };
  } else {
    return {
      type: true,
      msgLanguageKey: ""
    };
  }
};
// 邮箱校验
export const emailPattern = (v: any) => {
  if (validateEmail(v)) {
    return {
      type: true,
      msgLanguageKey: ""
    };
  } else {
    return {
      type: false,
      msgLanguageKey: "login.default.rules.validEmail"
    };
  }
};

export const agreementPrivacyFn = () => {
  location.href = "/help/privacy-policy";
};
export const agreementTermsFn = () => {
  location.href = "/help/terms-conditions";
};

export const isAbsoluteUrl = (url: string) => url.startsWith('http://') || url.startsWith('https://');
export const syncLoginState = async (url: any) => {
  let returnUrl = '';
  if (!url) {
    returnUrl = window.location.origin;
  } else {
    returnUrl = isAbsoluteUrl(url || "") ? url : new URL(decodeURIComponent(url), window.location.origin).toString();
  }
  if (isBeta || isProduction) {
    const res = await getSiteTicket({
      returnUrl
    });
    if (res.code && res.success) {
      const url = res.data?.redirectUrl;
      window.location.replace(url || returnUrl);
    }
  } else {
    window.location.replace(returnUrl);
  }
};